import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { ChangeEvent, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import Cookies from 'js-cookie';
import { usePdp } from '@context/PdpContext';
import { useCartUI } from '@context/CartContext';
import { useUI } from '@context/GlobalUIContext';
import { useStoresUI } from '@context/StoresContext';
import IconWrapper from '@components/ui/Icons';
import Button from '@components/ui/Button';
import StoreAvailability from '@components/product/StoreAvailability';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';
import {
  buildSPUHoursMessaging,
  buildStorelessSPUHoursMessaging,
} from '@utils/store/buildSPUHoursMessaging';
import { InventoryStatus } from '@adapters/bigcommerce/types/product';
import {
  SPU_COOKIE,
  STS_COOKIE,
  SPU_CHANGED_MARKER,
} from '@config/publicConstants';
import {
  FEATURE_FLAG_DISABLE_SPU,
  FEATURE_FLAG_NEXT_DAY,
  FEATURE_FLAG_NO_PICKUP_TIMES,
} from '@config/feature-flag';
import { shouldDisplayNotAvailableSPUMessage } from '@utils/pdp/displayNotAvailableSPUmessage';
import { determineStorePickupRestriction } from '@utils/store/spu';
import {
  trackDeliveryOptionInteraction,
  trackStoreInteraction,
} from '@utils/gtm/ga4/trackStoreInteraction';
import InStorePromotion from '@components/product/ProductBuyZone/InStorePromoMessage';

import styles from './ProductAvailability.module.scss';

interface ProductAvailabilityProps {
  abVariant: string | null;
}

const ProductAvailability = ({ abVariant }: ProductAvailabilityProps) => {
  const { openStockLevelModal } = useUI();

  const {
    selectedAvailability,
    selectedVariant,
    addToCartClicked,
    shippingRestrictions,
  } = usePdp();
  const { setDeliveryOption, deliveryOption } = useCartUI();
  const { favouriteStoreDetails, setLocationAndFavouriteStore } = useStoresUI();
  const { t } = useTranslation(['pdp', 'stores']);
  const isClient = useHasMounted();

  const handleClick = () => {
    trackStoreInteraction({
      data: {
        linkUrl: null,
        linkText: t('product.see.stores'),
      },
    });
    openStockLevelModal();
  };

  const freeShippingThreshold = process.env
    .NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_AMOUNT
    ? Number(process.env.NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_AMOUNT)
    : 50;

  const hasSelectedVariant = !!selectedVariant;
  const favouriteStoreId = favouriteStoreDetails?.data?.mec_store_id;
  const hasSetFavouriteStore =
    !!favouriteStoreDetails && typeof favouriteStoreId !== 'undefined';

  const storeInStock =
    hasSetFavouriteStore &&
    hasSelectedVariant &&
    !!selectedVariant?.storeInventory[favouriteStoreId]?.status &&
    !(
      selectedVariant?.storeInventory[favouriteStoreId]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[favouriteStoreId]?.status ===
        InventoryStatus.NOT_STOCKED
    );

  const stsDCInStock =
    hasSelectedVariant &&
    !!selectedVariant?.storeInventory[99]?.status &&
    !(
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.NOT_STOCKED
    );

  const isStorePickUpRestricted =
    determineStorePickupRestriction({
      storeUID: favouriteStoreDetails?.uid,
      restrictionLevel: shippingRestrictions?.restriction_level,
      defaultRestriction: hasSetFavouriteStore,
    }) ?? false;

  const stsShopInStock = !!selectedVariant?.hasStockAvailable;
  const stsInStock = stsDCInStock || stsShopInStock;

  type SpuMsgObjType = { day: null | string; key: string };
  const spuMessageObj: SpuMsgObjType = hasSetFavouriteStore
    ? buildSPUHoursMessaging(favouriteStoreDetails, t)
    : buildStorelessSPUHoursMessaging(t);

  const quickPickup = !!selectedVariant?.quickPickup;

  let showError;
  if (selectedVariant) {
    showError =
      deliveryOption !== 'shipping' &&
      addToCartClicked &&
      !favouriteStoreDetails;
  }

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    deliveryOptionText: string
  ) => {
    trackDeliveryOptionInteraction({
      data: {
        deliveryOption: deliveryOptionText,
      },
    });
    setDeliveryOption(event.target.value);
  };

  useEffect(() => {
    if (isStorePickUpRestricted && deliveryOption !== 'shipping') {
      Cookies.remove(SPU_COOKIE);
      Cookies.remove(STS_COOKIE);
      setDeliveryOption('shipping');
    }
  }, [deliveryOption, isStorePickUpRestricted, setDeliveryOption]);

  useEffect(() => {
    try {
      const spuCookie = Cookies.get(SPU_COOKIE);
      let storeName = '';
      if (spuCookie) {
        storeName = JSON.parse(spuCookie)?.storeName?.replace(
          SPU_CHANGED_MARKER,
          ''
        );
      }
      if (!!spuCookie) setDeliveryOption(storeName);
    } catch (err) {
      console.log(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deliveryOption !== 'shipping') {
      setDeliveryOption(favouriteStoreDetails?.data?.store_name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteStoreDetails]);

  const bikesOnlineCase =
    selectedVariant?.availableForSaleOnline && !selectedVariant?.quickPickup;

  const displayNotAvailableSPUMessage = shouldDisplayNotAvailableSPUMessage({
    isStorePickUpRestricted,
    hasSetFavouriteStore,
    hasSelectedVariant,
    storeInStock,
    stsInStock,
  });

  const StorePickUp = () => (
    <>
      <p className={styles.deliveryDescription}>
        {displayNotAvailableSPUMessage
          ? t('product.free.store.pickup.unavailable.at.location')
          : FEATURE_FLAG_NO_PICKUP_TIMES && FEATURE_FLAG_NEXT_DAY
          ? t('product.free.store.pickup.nextDay')
          : t('product.free.store.pickup')}
      </p>
      {selectedVariant ? (
        <div className={styles.storeInventoryContainer}>
          {favouriteStoreDetails ? (
            <>
              <Button
                className={styles.selectedStoreButton}
                variant="unstyled"
                size="small"
                label={favouriteStoreDetails.data.store_name}
                handleClick={() => {
                  setLocationAndFavouriteStore(favouriteStoreDetails.data.geo);
                  openStockLevelModal();
                }}
              />
              <StoreAvailability
                storeId={favouriteStoreDetails.data.mec_store_id}
                isModal={false}
                abVariant={abVariant}
              />
              <div>
                <Button
                  className={styles.seeAllStoresButton}
                  variant="unstyled"
                  size="small"
                  label={t('product.see.stores')}
                  handleClick={handleClick}
                />
              </div>
              <InStorePromotion />
            </>
          ) : (
            <>
              <Button
                className={styles.seeAllStoresButton}
                variant="unstyled"
                size="small"
                label={t('product.select.a.store')}
                handleClick={() => openStockLevelModal()}
              />
              {selectedAvailability &&
                !FEATURE_FLAG_NO_PICKUP_TIMES &&
                quickPickup &&
                !FEATURE_FLAG_DISABLE_SPU && (
                  <span className={styles.availabilityText}>
                    {' '}
                    {!FEATURE_FLAG_NEXT_DAY &&
                      t(`pdp:product.pickup.long.${spuMessageObj.key}`, {
                        day: spuMessageObj.day,
                      })}
                  </span>
                )}
              <InStorePromotion />
            </>
          )}
        </div>
      ) : (
        <p className={styles.availabilityText}>
          {t('product.select.size.availability')}
          <InStorePromotion />
        </p>
      )}
    </>
  );

  return (
    <div className={styles.availabilityContainer}>
      {selectedAvailability && (
        <p className={styles.deliveryLabel}>{t('pdp:product.delivery')}</p>
      )}
      <div
        className={cn(styles.optionsContainer, {
          [styles.inStoreOnlyOptionsContainer]: !selectedAvailability,
        })}
      >
        {selectedAvailability && (
          <div className={styles.iconWithText}>
            <label htmlFor="shippingRadio" className={styles.shippingOption}>
              <div>
                <div className={styles.deliveryIcons}>
                  <div className={styles.inputContainer}>
                    <input
                      type="radio"
                      id="shippingRadio"
                      value="shipping"
                      checked={deliveryOption === 'shipping'}
                      onChange={(event) =>
                        handleChange(event, t('product.shipping.message'))
                      }
                      className={cn(styles.selectButton)}
                    />
                  </div>
                  <IconWrapper
                    icon="freeshipping"
                    classNames={cn(styles.icon, styles.shippingIcon)}
                    width={22}
                  />
                </div>
              </div>
              <div className={styles.deliveryOptionContainer}>
                <p className={styles.deliveryDescription}>
                  {t('product.shipping.message')}
                </p>
                <div className={styles.availabilityMessage}>
                  <span className={styles.availabilityText}>
                    {bikesOnlineCase ||
                      t('product.ships.free', {
                        amount: freeShippingThreshold,
                      })}
                  </span>
                </div>
              </div>
            </label>
          </div>
        )}
        <div
          className={cn(styles.iconWithText, { [styles.hasError]: showError })}
        >
          <label htmlFor="spuRadio" className={styles.shippingOption}>
            <div>
              <div className={styles.deliveryIcons}>
                <div className={styles.inputContainer}>
                  {!isStorePickUpRestricted &&
                    hasSetFavouriteStore &&
                    (storeInStock || stsInStock) &&
                    selectedAvailability && (
                      <input
                        type="radio"
                        id="spuRadio"
                        value={favouriteStoreDetails?.data?.store_name}
                        checked={deliveryOption !== 'shipping'}
                        onChange={(event) =>
                          handleChange(event, t('product.free.store.pickup'))
                        }
                        className={cn(styles.selectButton)}
                      />
                    )}
                </div>
                <IconWrapper
                  icon="storepickup"
                  classNames={cn(styles.icon, styles.spuIcon)}
                  width={22}
                />
              </div>
            </div>
            <div className={styles.deliveryOptionContainer}>
              {isClient ? (
                // Loading this section on the client as it's dependent on favourite store selected (which is based on a cookie). Prevents hydration issues.
                <StorePickUp />
              ) : (
                <>
                  <p className={styles.deliveryDescription}>
                    <Skeleton width={200} height={20} />
                  </p>
                  <p className={styles.availabilityText}>
                    <Skeleton width={200} count={2} />
                  </p>
                </>
              )}
            </div>
          </label>
        </div>
      </div>
      {showError && (
        <p className={styles.errorMessage}>{t('product.store.error')}</p>
      )}
    </div>
  );
};

export default ProductAvailability;
